import React, {useEffect, useState} from "react";
import {css, keyframes, Theme, useTheme} from "@emotion/react";
import styled from "@emotion/styled";

import {borderRadius, calculateRemSize, m, mb, p} from "@pg-design/helpers-css";
import {BrandEyeIcon, ChevronRightIcon} from "@pg-design/icons-module";
import {Image} from "@pg-design/image-module";
import {Text} from "@pg-design/text-module";
import {useActiveElementId} from "@pg-mono/hooks";

import {OfferBoxBase} from "../../../atoms/OfferBoxBase";
import {DesktopText} from "../../../atoms/typography/DesktopText";
import {getThumbnailLink} from "../../../utils/get_thumbnail_link";
import {ISurpriseCreation} from "../../types/ISurpriseCreation";

interface ISurpriseCreationDisplayProps {
    creation: ISurpriseCreation;
    url: string;
    htmlElementId: string;
    referenceElementId?: string;
    onElementActive?: () => void;
    onCreationClick?: () => void;
    onBackfaceClick?: () => void;
}

export const SurpriseCreationDisplay = (props: ISurpriseCreationDisplayProps) => {
    const {
        htmlElementId,
        onElementActive,
        onCreationClick,
        onBackfaceClick,
        creation: {image_url, logo_url, title, description},
        url
    } = props;

    const isElementActive = useActiveElementId([htmlElementId], {rootMargin: "-20%", threshold: 0.1}) === htmlElementId;
    const theme = useTheme();
    const [isFlipped, setIsFlipped] = useState(false);
    const [height, setMinHeight] = useState(0);

    useEffect(() => {
        if (props.referenceElementId) {
            const firstListElement = document.getElementById(props.referenceElementId);
            if (firstListElement && !height) {
                setMinHeight(firstListElement.offsetHeight);
            }
        }
    }, [props.referenceElementId]);

    useEffect(() => {
        if (isElementActive && onElementActive) {
            onElementActive();
        }
    }, [isElementActive]);

    const onImageAdCreationClick = () => {
        if (onCreationClick) {
            onCreationClick();
        }
        window.open(url, "_blank");
    };

    const onOverlayClick = () => {
        if (onBackfaceClick) {
            onBackfaceClick();
        }

        setIsFlipped(true);
    };

    const primaryColor = theme.colors.primary;

    return (
        <div css={flipContainer}>
            <div css={() => flipContainerInner(isFlipped)}>
                <div onClick={onOverlayClick} css={cardBackface}>
                    <div css={backfaceTextWrapper}>
                        <DesktopText variant="headline_3" fontFamily="secondary">
                            Mamy dla Ciebie coś specjalnego
                        </DesktopText>
                        <div css={backfaceIconWrapper}>
                            <BrandEyeIcon size="16" fill="#0E1B47" />
                        </div>
                        <Text variant="button_big">odkryj</Text>
                    </div>
                </div>
                <CardFrontFace style={{height, maxHeight: height}} onClick={onImageAdCreationClick}>
                    <Image css={m(0, 0, 1, 2)} alt="logo reklamodawcy" width="80" height="60" src={getThumbnailLink(logo_url, 80, 60)} />

                    <Image
                        src={getThumbnailLink(image_url, 375, 211)}
                        alt="zdjęcie reklamodawcy"
                        width="375px"
                        height="211px"
                        loading="lazy"
                        ratio={{xs: "321/200"}}
                    />

                    <div css={creationInfoHolder}>
                        <DesktopText css={mb(2)} as="p" variant="headline_6">
                            {title}
                        </DesktopText>
                        <DesktopText css={descriptionText} as="p" variant="info_txt_1">
                            {description}
                        </DesktopText>
                    </div>

                    <ChevronRightIcon wrapperColor={primaryColor} wrapperType="circle" wrapperSize="3.2" size="2" css={cardArrowIcon} />
                </CardFrontFace>
            </div>
        </div>
    );
};

const descriptionText = css`
    word-break: break-word;
    hyphens: auto;
`;

const creationInfoHolder = css`
    ${p(3, 0, 0, 2)};
    position: relative;
    min-height: 17.3rem;
`;

const cardArrowIcon = css`
    margin-left: auto;
    position: absolute;
    bottom: ${calculateRemSize(2)};
    right: ${calculateRemSize(2)};
`;

const cardBackface = (theme: Theme) => css`
    background-color: ${theme.colors.primary};
    ${p(8, 4, 8, 4)};
    width: 100%;
    height: 100%;
    position: absolute;
    backface-visibility: hidden;
    ${borderRadius(2)}
    font-size: 1.6rem;
    cursor: pointer;
`;

const backfaceTextWrapper = css`
    text-align: center;
`;

const backfaceIconWrapper = css`
    height: 8.7rem;
    width: 8.7rem;
    margin: 6rem auto 9rem auto;
    pointer-events: none;

    & svg {
        margin-top: 50%;
    }
`;

const CardFrontFace = styled(OfferBoxBase)`
    position: relative;
    backface-visibility: hidden;
    transform: rotateY(180deg);
    overflow: hidden;
    cursor: pointer;
    ${p(2)}
`;

const flipContainer = css`
    perspective: 1000px;
    position: relative;
    background: transparent;
    min-width: 288px;
    max-width: 375px;
    min-height: 27.3rem;
    height: 100%;
    margin: 0 auto;
`;

const flipContainerInner = (isFlipped: boolean) => css`
    top: 0;
    width: 100%;
    height: 100%;
    position: relative;
    transition: transform 2s;
    transform-style: preserve-3d;

    ${isFlipped &&
    css`
        transform: rotateY(180deg);
        animation: ${flipAnimation} 0.5s cubic-bezier(0.455, 0.03, 0.515, 0.955) both;
    `};
`;

const flipAnimation = keyframes`
  0% {
    transform: rotateY(0);
  }

  100% {
    transform: rotateY(-180deg);
  }
`;
import React, {useEffect, useState} from "react";
import {css, keyframes, Theme, useTheme} from "@emotion/react";
import styled from "@emotion/styled";

import {borderRadius, calculateRemSize, m, mb, p} from "@pg-design/helpers-css";
import {BrandEyeIcon, ChevronRightIcon} from "@pg-design/icons-module";
import {Image} from "@pg-design/image-module";
import {Text} from "@pg-design/text-module";
import {useActiveElementId} from "@pg-mono/hooks";

import {OfferBoxBase} from "../../../atoms/OfferBoxBase";
import {DesktopText} from "../../../atoms/typography/DesktopText";
import {getThumbnailLink} from "../../../utils/get_thumbnail_link";
import {ISurpriseCreation} from "../../types/ISurpriseCreation";

interface ISurpriseCreationDisplayProps {
    creation: ISurpriseCreation;
    url: string;
    htmlElementId: string;
    referenceElementId?: string;
    onElementActive?: () => void;
    onCreationClick?: () => void;
    onBackfaceClick?: () => void;
}

export const SurpriseCreationDisplay = (props: ISurpriseCreationDisplayProps) => {
    const {
        htmlElementId,
        onElementActive,
        onCreationClick,
        onBackfaceClick,
        creation: {image_url, logo_url, title, description},
        url
    } = props;

    const isElementActive = useActiveElementId([htmlElementId], {rootMargin: "-20%", threshold: 0.1}) === htmlElementId;
    const theme = useTheme();
    const [isFlipped, setIsFlipped] = useState(false);
    const [height, setMinHeight] = useState(0);

    useEffect(() => {
        if (props.referenceElementId) {
            const firstListElement = document.getElementById(props.referenceElementId);
            if (firstListElement && !height) {
                setMinHeight(firstListElement.offsetHeight);
            }
        }
    }, [props.referenceElementId]);

    useEffect(() => {
        if (isElementActive && onElementActive) {
            onElementActive();
        }
    }, [isElementActive]);

    const onImageAdCreationClick = () => {
        if (onCreationClick) {
            onCreationClick();
        }
        window.open(url, "_blank");
    };

    const onOverlayClick = () => {
        if (onBackfaceClick) {
            onBackfaceClick();
        }

        setIsFlipped(true);
    };

    const primaryColor = theme.colors.primary;

    return (
        <div css={flipContainer}>
            <div css={() => flipContainerInner(isFlipped)}>
                <div onClick={onOverlayClick} css={cardBackface}>
                    <div css={backfaceTextWrapper}>
                        <DesktopText variant="headline_3" fontFamily="secondary">
                            Mamy dla Ciebie coś specjalnego
                        </DesktopText>
                        <div css={backfaceIconWrapper}>
                            <BrandEyeIcon size="16" fill="#0E1B47" />
                        </div>
                        <Text variant="button_big">odkryj</Text>
                    </div>
                </div>
                <CardFrontFace style={{height, maxHeight: height}} onClick={onImageAdCreationClick}>
                    <Image css={m(0, 0, 1, 2)} alt="logo reklamodawcy" width="80" height="60" src={getThumbnailLink(logo_url, 80, 60)} />

                    <Image
                        src={getThumbnailLink(image_url, 375, 211)}
                        alt="zdjęcie reklamodawcy"
                        width="375px"
                        height="211px"
                        loading="lazy"
                        ratio={{xs: "321/200"}}
                    />

                    <div css={creationInfoHolder}>
                        <DesktopText css={mb(2)} as="p" variant="headline_6">
                            {title}
                        </DesktopText>
                        <DesktopText css={descriptionText} as="p" variant="info_txt_1">
                            {description}
                        </DesktopText>
                    </div>

                    <ChevronRightIcon wrapperColor={primaryColor} wrapperType="circle" wrapperSize="3.2" size="2" css={cardArrowIcon} />
                </CardFrontFace>
            </div>
        </div>
    );
};

const descriptionText = css`
    word-break: break-word;
    hyphens: auto;
`;

const creationInfoHolder = css`
    ${p(3, 0, 0, 2)};
    position: relative;
    min-height: 17.3rem;
`;

const cardArrowIcon = css`
    margin-left: auto;
    position: absolute;
    bottom: ${calculateRemSize(2)};
    right: ${calculateRemSize(2)};
`;

const cardBackface = (theme: Theme) => css`
    background-color: ${theme.colors.primary};
    ${p(8, 4, 8, 4)};
    width: 100%;
    height: 100%;
    position: absolute;
    backface-visibility: hidden;
    ${borderRadius(2)}
    font-size: 1.6rem;
    cursor: pointer;
`;

const backfaceTextWrapper = css`
    text-align: center;
`;

const backfaceIconWrapper = css`
    height: 8.7rem;
    width: 8.7rem;
    margin: 6rem auto 9rem auto;
    pointer-events: none;

    & svg {
        margin-top: 50%;
    }
`;

const CardFrontFace = styled(OfferBoxBase)`
    position: relative;
    backface-visibility: hidden;
    transform: rotateY(180deg);
    overflow: hidden;
    cursor: pointer;
    ${p(2)}
`;

const flipContainer = css`
    perspective: 1000px;
    position: relative;
    background: transparent;
    min-width: 288px;
    max-width: 375px;
    min-height: 27.3rem;
    height: 100%;
    margin: 0 auto;
`;

const flipContainerInner = (isFlipped: boolean) => css`
    top: 0;
    width: 100%;
    height: 100%;
    position: relative;
    transition: transform 2s;
    transform-style: preserve-3d;

    ${isFlipped &&
    css`
        transform: rotateY(180deg);
        animation: ${flipAnimation} 0.5s cubic-bezier(0.455, 0.03, 0.515, 0.955) both;
    `};
`;

const flipAnimation = keyframes`
  0% {
    transform: rotateY(0);
  }

  100% {
    transform: rotateY(-180deg);
  }
`;
